import React, { useState, useEffect, useRef } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Image, 
  Upload, 
  Trash2, 
  Loader2, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  FileImage,
  Users,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const NEEDS_LABELS = {
  'life_insurance': 'Life Insurance',
  'will_trust': 'Will / Trust',
  'child_education': 'Child Education Planning',
  'retirement': 'Retirement Planning',
};

const STATUS_OPTIONS = [
  { value: 'NEW', label: 'New', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  { value: 'IN_PROGRESS', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  { value: 'COMPLETED', label: 'Completed', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300' },
];

export default function FlyerManagement() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('flyer');
  const [flyer, setFlyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const fileInputRef = useRef(null);
  
  // Flyers state (multiple)
  const [flyers, setFlyers] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  
  // Requests state
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const breadcrumbItems = [
    { label: 'Admin', href: '/admin' },
    { label: 'Flyer Management', href: '/admin/flyer' }
  ];

  // Fetch all flyers
  const fetchFlyers = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/flyers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFlyers(data.flyers || []);
        setActiveCount(data.active_count || 0);
      }
    } catch (error) {
      console.error('Error fetching flyers:', error);
      toast.error('Failed to load flyers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch requests
  const fetchRequests = async () => {
    try {
      setLoadingRequests(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      let url = `${backendUrl}/api/admin/financial-planning/requests?limit=100`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    fetchFlyers();
  }, []);

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchRequests();
    }
  }, [activeTab, statusFilter]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, or WEBP.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);

    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('is_active', 'true');
      
      const response = await fetch(`${backendUrl}/api/admin/flyer/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      toast.success('Flyer uploaded successfully!');
      fetchFlyers(); // Refresh the list
    } catch (error) {
      console.error('Error uploading flyer:', error);
      toast.error(error.message || 'Failed to upload flyer');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Toggle flyer active status
  const handleToggleActive = async (flyerId, currentStatus) => {
    setUpdatingId(flyerId);

    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/flyer/${flyerId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update flyer status');
      }
      
      // Update local state
      setFlyers(prev => prev.map(f => 
        f.id === flyerId ? { ...f, is_active: !currentStatus } : f
      ));
      setActiveCount(prev => currentStatus ? prev - 1 : prev + 1);
      toast.success(`Flyer ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error updating flyer:', error);
      toast.error('Failed to update flyer status');
    } finally {
      setUpdatingId(null);
    }
  };

  // Delete flyer
  const handleDelete = async (flyerId) => {
    if (!confirm('Are you sure you want to delete this flyer?')) return;

    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/flyer/${flyerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete flyer');
      }
      
      // Update local state
      const deletedFlyer = flyers.find(f => f.id === flyerId);
      setFlyers(prev => prev.filter(f => f.id !== flyerId));
      if (deletedFlyer?.is_active) {
        setActiveCount(prev => prev - 1);
      }
      toast.success('Flyer deleted successfully');
    } catch (error) {
      console.error('Error deleting flyer:', error);
      toast.error('Failed to delete flyer');
    }
  };

  // Update request status
  const handleStatusUpdate = async (requestId, newStatus) => {
    setUpdatingStatus(requestId);

    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/admin/financial-planning/requests/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update status');
      }
      
      // Update local state
      setRequests(prev => prev.map(req => 
        req.id === requestId ? { ...req, status: newStatus } : req
      ));
      
      toast.success('Status updated');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status);
    return (
      <Badge className={cn("font-medium", statusOption?.color || '')}>
        {statusOption?.label || status}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <DashboardLayout role={user?.role} title="Dashboard Flyer" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={breadcrumbItems} />
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Dashboard Flyer</h1>
            <p className="text-muted-foreground mt-1">
              Manage promotional flyer displayed on user dashboard
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="flyer" className="flex items-center gap-2">
              <FileImage className="h-4 w-4" />
              Flyer Settings
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Requests
              {requests.filter(r => r.status === 'NEW').length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {requests.filter(r => r.status === 'NEW').length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Flyer Settings Tab */}
          <TabsContent value="flyer" className="mt-6">
            <div className="space-y-6">
              {/* Upload Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Upload New Flyer
                  </CardTitle>
                  <CardDescription>
                    Upload promotional images (JPG, PNG, WEBP). Max size: 5MB. You can have multiple active flyers - they will rotate randomly for users.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="flyer-upload"
                  />
                  
                  <div 
                    className={cn(
                      "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                      "hover:border-primary hover:bg-muted/50",
                      uploading && "opacity-50 pointer-events-none"
                    )}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-muted-foreground">Uploading...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Image className="h-8 w-8 text-muted-foreground" />
                        <p className="font-medium">Click to upload flyer image</p>
                        <p className="text-sm text-muted-foreground">
                          JPG, PNG, WEBP (max 5MB) • Recommended: 600x200px
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* All Flyers Grid */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Eye className="h-5 w-5" />
                        All Flyers
                      </CardTitle>
                      <CardDescription>
                        {flyers.length} flyer{flyers.length !== 1 ? 's' : ''} • {activeCount} active (rotating)
                      </CardDescription>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={fetchFlyers}
                      disabled={loading}
                    >
                      <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : flyers.length === 0 ? (
                    <div className="text-center p-8 text-muted-foreground">
                      <FileImage className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No flyers uploaded yet</p>
                      <p className="text-sm">Upload an image above to get started</p>
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {flyers.map((flyer) => (
                        <div 
                          key={flyer.id} 
                          className={cn(
                            "relative rounded-lg border overflow-hidden transition-all",
                            flyer.is_active ? "ring-2 ring-green-500" : "opacity-60"
                          )}
                        >
                          {/* Image */}
                          <div className="relative bg-muted">
                            <img
                              src={flyer.image_url}
                              alt="Flyer"
                              className="w-full h-32 object-contain"
                            />
                            {flyer.is_active && (
                              <Badge className="absolute top-2 right-2 bg-green-500">
                                Active
                              </Badge>
                            )}
                          </div>
                          
                          {/* Controls */}
                          <div className="p-3 space-y-2">
                            <div className="text-xs text-muted-foreground">
                              Added: {formatDate(flyer.created_at)}
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={flyer.is_active}
                                  onCheckedChange={() => handleToggleActive(flyer.id, flyer.is_active)}
                                  disabled={updatingId === flyer.id}
                                />
                                <span className="text-sm">
                                  {flyer.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                              
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDelete(flyer.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Financial Planning Requests</CardTitle>
                    <CardDescription>
                      User submissions from dashboard flyer
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        {STATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={fetchRequests}
                      disabled={loadingRequests}
                    >
                      <RefreshCw className={cn("h-4 w-4", loadingRequests && "animate-spin")} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingRequests ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center p-8 text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No requests found</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Needs</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell className="font-medium">
                            {request.full_name}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 text-sm">
                                <Mail className="h-3 w-3" />
                                {request.email}
                              </div>
                              {request.phone && (
                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <Phone className="h-3 w-3" />
                                  {request.phone}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(request.needs || []).slice(0, 2).map(need => (
                                <Badge key={need} variant="outline" className="text-xs">
                                  {NEEDS_LABELS[need] || need}
                                </Badge>
                              ))}
                              {(request.needs || []).length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{request.needs.length - 2}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={request.status}
                              onValueChange={(value) => handleStatusUpdate(request.id, value)}
                              disabled={updatingStatus === request.id}
                            >
                              <SelectTrigger className="w-[130px] h-8">
                                <SelectValue>
                                  {getStatusBadge(request.status)}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(request.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedRequest(request)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Request Detail Modal */}
        <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Request Details</DialogTitle>
              <DialogDescription>
                Submitted {selectedRequest && formatDate(selectedRequest.created_at)}
              </DialogDescription>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Full Name</Label>
                    <p className="font-medium">{selectedRequest.full_name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                  </div>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{selectedRequest.email}</p>
                </div>
                
                {selectedRequest.phone && (
                  <div>
                    <Label className="text-muted-foreground">Phone</Label>
                    <p className="font-medium">{selectedRequest.phone}</p>
                  </div>
                )}
                
                <div>
                  <Label className="text-muted-foreground">Needs</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(selectedRequest.needs || []).map(need => (
                      <Badge key={need} variant="secondary">
                        {NEEDS_LABELS[need] || need}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {selectedRequest.notes && (
                  <div>
                    <Label className="text-muted-foreground">Notes</Label>
                    <p className="text-sm bg-muted p-3 rounded-md mt-1">
                      {selectedRequest.notes}
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
